import React from 'react';
import { Badge, type BadgeProps } from "@/components/ui/badge";
import {
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  RefreshCw,
  Play,
  Pause,
  MailQuestion, // Example for email status
  Paperclip, // Example for attachment status
  CircleDollarSign, // Example for payment status
  ShoppingCart, // Example for order status
  Users, // Example for user status
  UserPlus,
  UserX,
  FileCheck,
  Ban,
  // Add other icons used across different status badges
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Define possible contexts for statuses to avoid ambiguity
// Add more contexts as needed (e.g., 'user', 'task', 'participant', 'voucher')
type StatusContext = 
  | 'invoice' 
  | 'subscription' 
  | 'job' 
  | 'job-execution' 
  | 'email' 
  | 'order'
  | 'payment'
  | 'session'
  | 'customer'
  | 'voucher'
  | 'participant'
  | 'certificate'
  | 'class' // Added context for main class status
  | 'class_session' // Added context for class session status
  | 'general';

interface StatusBadgeProps {
  status: string | null | undefined;
  context?: StatusContext;
  className?: string;
}

interface BadgeStyle {
  variant: BadgeProps["variant"];
  icon?: React.ReactNode;
  text: string;
  className?: string;
}

// --- Status Mapping --- 
// Central place to define styles for different statuses and contexts
// Add more statuses and contexts as you refactor other components
const statusMap: Record<StatusContext, Record<string, BadgeStyle>> = {
  invoice: {
    paid: { variant: 'success', icon: <CheckCircle className="h-3 w-3" />, text: 'Pago' },
    pending: { variant: 'warning', icon: <Clock className="h-3 w-3" />, text: 'Pendente' },
    overdue: { variant: 'destructive', icon: <AlertCircle className="h-3 w-3" />, text: 'Vencida' },
    cancelled: { variant: 'secondary', icon: <XCircle className="h-3 w-3" />, text: 'Cancelada' },
    draft: { variant: 'outline', text: 'Rascunho' },
  },
  subscription: {
    active: { variant: 'success', icon: <CheckCircle className="h-3 w-3" />, text: 'Ativa' },
    trialing: { variant: 'default', className: 'bg-blue-100 text-blue-800', text: 'Trial' },
    past_due: { variant: 'warning', icon: <AlertCircle className="h-3 w-3" />, text: 'Pendente' },
    canceled: { variant: 'secondary', icon: <XCircle className="h-3 w-3" />, text: 'Cancelada' },
    unpaid: { variant: 'destructive', icon: <CircleDollarSign className="h-3 w-3" />, text: 'Não Paga' }, // Example
    paused: { variant: 'outline', icon: <Pause className="h-3 w-3" />, text: 'Pausada' }, // Example
  },
  job: {
    active: { variant: 'success', icon: <Play className="h-3 w-3" />, text: 'Ativo' },
    paused: { variant: 'secondary', icon: <Pause className="h-3 w-3" />, text: 'Pausado' },
    draft: { variant: 'outline', text: 'Rascunho' },
    completed: { variant: 'default', icon: <CheckCircle className="h-3 w-3" />, text: 'Concluído' }, // Maybe differentiate from execution?
  },
  'job-execution': {
    pending: { variant: 'outline', icon: <Clock className="h-3 w-3" />, text: 'Pendente' },
    running: { variant: 'default', className:'bg-blue-100 text-blue-800', icon: <RefreshCw className="h-3 w-3 animate-spin" />, text: 'Em execução' },
    completed: { variant: 'success', icon: <CheckCircle className="h-3 w-3" />, text: 'Concluído' },
    failed: { variant: 'destructive', icon: <XCircle className="h-3 w-3" />, text: 'Falhou' },
  },
  email: {
    sent: { variant: 'success', icon: <CheckCircle className="h-3 w-3" />, text: 'Enviado' },
    delivered: { variant: 'success', icon: <CheckCircle className="h-3 w-3" />, text: 'Entregue' },
    failed: { variant: 'destructive', icon: <XCircle className="h-3 w-3" />, text: 'Falhou' },
    queued: { variant: 'outline', icon: <Clock className="h-3 w-3" />, text: 'Na fila' },
    opened: { variant: 'default', className:'bg-blue-100 text-blue-800', text: 'Aberto' },
    clicked: { variant: 'default', className:'bg-purple-100 text-purple-800', text: 'Clicado' },
  },
  order: {
    // Example order statuses
    pending: { variant: 'warning', icon: <Clock className="h-3 w-3" />, text: 'Pendente' },
    processing: { variant: 'default', className:'bg-blue-100 text-blue-800', icon: <RefreshCw className="h-3 w-3 animate-spin" />, text: 'Processando' },
    shipped: { variant: 'default', className:'bg-purple-100 text-purple-800', text: 'Enviado' },
    delivered: { variant: 'success', icon: <CheckCircle className="h-3 w-3" />, text: 'Entregue' },
    cancelled: { variant: 'secondary', icon: <XCircle className="h-3 w-3" />, text: 'Cancelado' },
  },
  payment: {
    // Example payment statuses
    pending: { variant: 'warning', icon: <Clock className="h-3 w-3" />, text: 'Pendente' },
    paid: { variant: 'success', icon: <CheckCircle className="h-3 w-3" />, text: 'Pago' },
    failed: { variant: 'destructive', icon: <XCircle className="h-3 w-3" />, text: 'Falhou' },
    refunded: { variant: 'secondary', text: 'Reembolsado' },
  },
  session: { // Added context for class sessions
    draft: { variant: 'outline', icon: <Clock className="h-3 w-3" />, text: 'Rascunho' },
    expected: { variant: 'warning', icon: <Clock className="h-3 w-3" />, text: 'Prevista', className: 'bg-amber-100 text-amber-800' }, // Using warning variant with amber colors
    confirmed: { variant: 'success', icon: <CheckCircle className="h-3 w-3" />, text: 'Confirmada' },
    running: { variant: 'default', icon: <Play className="h-3 w-3" />, text: 'Em Andamento', className: 'bg-blue-100 text-blue-800' }, // Added 'running' status
    done: { variant: 'default', icon: <CheckCircle className="h-3 w-3" />, text: 'Realizada', className: 'bg-gray-100 text-gray-800' }, // Using default variant, gray style
    cancelled: { variant: 'secondary', icon: <XCircle className="h-3 w-3" />, text: 'Cancelada' },
  },
  customer: { // Added context for customer status
    active: { variant: 'success', icon: <CheckCircle className="h-3 w-3" />, text: 'Ativo' },
    inactive: { variant: 'outline', text: 'Inativo', className: 'bg-gray-100 text-gray-700 border-gray-300' }, // Keep specific style from original file
    pending: { variant: 'warning', icon: <Clock className="h-3 w-3" />, text: 'Pendente', className: 'bg-amber-100 text-amber-700 border-amber-200' }, // Keep specific style
    suspended: { variant: 'destructive', icon: <AlertCircle className="h-3 w-3" />, text: 'Suspenso', className: 'bg-red-100 text-red-700 border-red-300' }, // Keep specific style
  },
  voucher: { // Added context for voucher status
    active: { variant: 'success', icon: <CheckCircle className="h-3 w-3" />, text: 'Ativo' },
    claimed: { variant: 'default', icon: <CheckCircle className="h-3 w-3" />, text: 'Utilizado', className: 'bg-blue-100 text-blue-800' }, // Using 'claimed' instead of 'used', blue style
    inactive: { variant: 'secondary', text: 'Inativo' }, // Added inactive based on likely needs
    expired: { variant: 'secondary', icon: <Clock className="h-3 w-3" />, text: 'Expirado' }, // Added expired based on likely needs
    cancelled: { variant: 'destructive', icon: <XCircle className="h-3 w-3" />, text: 'Cancelado' },
  },
  participant: { // Added context for class participant status
    registered: { variant: 'default', icon: <UserPlus className="h-3 w-3" />, text: 'Registrado', className: 'bg-blue-100 text-blue-800' },
    confirmed: { variant: 'success', icon: <CheckCircle className="h-3 w-3" />, text: 'Confirmado' },
    cancelled: { variant: 'destructive', icon: <XCircle className="h-3 w-3" />, text: 'Cancelado' },
    noshow: { variant: 'secondary', icon: <UserX className="h-3 w-3" />, text: 'Não Compareceu' }, // Added potential status
  },
  certificate: { // Added context for certificate status
    draft: { variant: 'outline', icon: <Clock className="h-3 w-3" />, text: 'Rascunho' },
    issued: { variant: 'success', icon: <FileCheck className="h-3 w-3" />, text: 'Emitido' },
    revoked: { variant: 'destructive', icon: <Ban className="h-3 w-3" />, text: 'Revogado' },
    pending: { variant: 'warning', icon: <Clock className="h-3 w-3" />, text: 'Pendente' }, // Added pending
  },
  class: { // Main class status
    draft: { variant: 'outline', icon: <Clock className="h-3 w-3" />, text: 'Rascunho' },
    scheduled: { variant: 'warning', icon: <Clock className="h-3 w-3" />, text: 'Agendada', className: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
    cancelled: { variant: 'destructive', icon: <XCircle className="h-3 w-3" />, text: 'Cancelada' },
    finished: { variant: 'success', icon: <CheckCircle className="h-3 w-3" />, text: 'Finalizada', className: 'bg-green-100 text-green-800 border-green-300' }
  },
  class_session: { // Class session status
    draft: { variant: 'outline', icon: <Clock className="h-3 w-3" />, text: 'Rascunho' },
    expected: { variant: 'warning', icon: <Clock className="h-3 w-3" />, text: 'Prevista' },
    confirmed: { variant: 'default', icon: <Clock className="h-3 w-3" />, text: 'Confirmada', className: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
    cancelled: { variant: 'destructive', icon: <XCircle className="h-3 w-3" />, text: 'Cancelada' },
    rescheduled: { variant: 'default', icon: <RefreshCw className="h-3 w-3" />, text: 'Reagendada', className: 'bg-pink-100 text-pink-800 border-pink-300' },
    done: { variant: 'default', icon: <CheckCircle className="h-3 w-3" />, text: 'Realizada', className: 'bg-green-100 text-green-800 border-green-300' }
  },
  general: { // Fallback for common statuses or when context is unclear
    active: { variant: 'success', text: 'Ativo' },
    inactive: { variant: 'secondary', text: 'Inativo' },
    true: { variant: 'success', text: 'Sim' },
    false: { variant: 'secondary', text: 'Não' },
    yes: { variant: 'success', text: 'Sim' },
    no: { variant: 'secondary', text: 'Não' },
    completed: { variant: 'success', text: 'Completo' },
    pending: { variant: 'warning', text: 'Pendente' },
    draft: { variant: 'outline', text: 'Rascunho' },
    error: { variant: 'destructive', text: 'Erro' },
    failed: { variant: 'destructive', text: 'Falhou' },
    cancelled: { variant: 'secondary', text: 'Cancelado' },
    canceled: { variant: 'secondary', text: 'Cancelado' }, // Common misspelling
  },
};

const defaultStyle: BadgeStyle = {
  variant: 'outline',
  text: 'Desconhecido',
};

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, context = 'general', className }) => {
  if (status === null || status === undefined || status === '') {
    return null; // Or return a default badge for empty/null status if preferred
    // return <Badge variant="outline" className={className}>N/A</Badge>;
  }

  const lowerCaseStatus = String(status).toLowerCase();
  
  // Get the style mapping for the given context, or fallback to general context
  const contextMap = statusMap[context] || statusMap.general;
  
  // Get the specific style for the status within the context
  const style = contextMap[lowerCaseStatus] || statusMap.general[lowerCaseStatus] || defaultStyle;

  return (
    <Badge 
      variant={style.variant} 
      className={cn('whitespace-nowrap', style.className, className)} // Combine base, style-specific, and prop classes
    >
      {style.icon && <span className="mr-1">{style.icon}</span>}
      {style.text}
    </Badge>
  );
};
